import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import classNames from 'classnames';

import type { RosterPlayer } from '~/types/screen/regie';
import { safePrimary, safeSecondary } from '~/services/branding/brandingColors';

import styles from './PublicLobbyView.module.css';

export type PublicLobbyViewProps = {
    /** Nom de la soirée (vide → nom de l'établissement). */
    eventName: string;
    venueName: string;
    /**
     * Marque de l'établissement (formule Pro « Ma marque », activée) : son logo en haut à
     * gauche et ses couleurs. Sans marque : logo et couleurs Unboared.
     */
    brand?: { logo: string | null; primary: string; secondary: string } | null;
    /** Lien des joueurs (null tant que la session n'est pas prête). */
    link: string | null;
    players: RosterPlayer[];
    ticker?: string;
    /** Message plein écran à la place de l'accueil. */
    message?: string | null;
    /** Aperçu dans la régie : pas d'animation, pas de lecture d'écran. */
    preview?: boolean;
};

const MAX_AVATARS = 24;
const UNBOARED_LOGO = '/assets/images/logo-unboared.png';

/**
 * Ce que voit la salle sur l'écran public entre deux jeux : nom de la soirée, QR
 * des joueurs, joueurs déjà là, bandeau. Le même rendu sert à l'écran public et
 * à l'aperçu de la régie (tailles en unités de conteneur).
 */
export function PublicLobbyView({
    eventName,
    venueName,
    brand,
    link,
    players,
    ticker,
    message,
    preview = false,
}: PublicLobbyViewProps) {
    const { t } = useTranslation();
    const title = eventName.trim() || venueName;
    const shown = players.slice(0, MAX_AVATARS);
    const more = players.length - shown.length;
    // Couleurs ajustées comme sur le reste de la TV (texte blanc lisible sur la principale,
    // secondaire lisible sur le fond nuit).
    const colors = brand
        ? ({
              '--lobby-a': safePrimary(brand.primary),
              '--lobby-b': safeSecondary(brand.secondary),
          } as React.CSSProperties)
        : undefined;
    return (
        <div
            className={classNames(styles.stage, preview && styles.preview)}
            style={colors}
            aria-hidden={preview || undefined}
        >
            <div className={styles.top}>
                <img
                    className={styles.logo}
                    src={brand?.logo || UNBOARED_LOGO}
                    alt={brand?.logo ? venueName : 'Unboared'}
                />
            </div>
            {message ? (
                <div className={styles.message}>{message}</div>
            ) : (
                <div className={styles.lobby}>
                    <div className={styles.qrCol}>
                        <span className={styles.qr}>
                            {link ? <QRCodeSVG value={link} size={512} /> : <span className={styles.qrWait} />}
                        </span>
                        <span className={styles.scan}>{t('regie.public.scan')}</span>
                    </div>
                    <div className={styles.textCol}>
                        <span className={styles.kicker}>
                            {eventName.trim() ? venueName : t('regie.public.tonight')}
                        </span>
                        <span className={styles.title}>{title}</span>
                        {players.length > 0 ? (
                            <>
                                <span className={styles.avatars}>
                                    {shown.map((p) => (
                                        <span
                                            key={p.id}
                                            className={styles.avatar}
                                            style={{ background: p.color || undefined }}
                                            title={p.name}
                                        >
                                            {p.avatar ? (
                                                <img src={p.avatar} alt="" />
                                            ) : (
                                                p.name.slice(0, 1).toUpperCase()
                                            )}
                                        </span>
                                    ))}
                                    {more > 0 && <span className={styles.more}>+{more}</span>}
                                </span>
                                <span className={styles.count}>
                                    {t('regie.public.players', { count: players.length })}
                                </span>
                            </>
                        ) : (
                            <span className={styles.count}>{t('regie.public.waiting')}</span>
                        )}
                    </div>
                </div>
            )}
            {ticker?.trim() && (
                <div className={styles.ticker}>
                    <div className={styles.tickerTrack}>
                        <span>{ticker}</span>
                        <span aria-hidden="true">{ticker}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
